import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChineseSurnamePicker } from '@fs/zion-chinese-shared'
import ZionForm, { useZionForm, FormTextField } from '@fs/zion-form'
import { Button, DialogOverlayActions, Paragraph, Separator, Row } from '@fs/zion-ui'

import { useChineseSurnameInfo } from '../../providers/ChineseSurnameProvider'
import { useMemoryBook } from '../../providers/MemoryBookProvider'
import { SURNAMES_WITH_COMPLETE_DATA_AND_IMAGES } from '../../services/ChineseSurnamesAPI/chineseSurnamesAPI'
import { fetchSurnameData } from '../../services/SurnameAPI/fetchSurnameData'
import calculatePageParagraphs from '../../helpers/calculatePageParagraphs/calculatePageParagraphs'
import { processHtmlDefinition } from '../../helpers/processHtmlDefinition/processHtmlDefinition'
import useDeviceSize from '../../hooks/useDeviceSize/useDeviceSize'

function FormActions({ overlay, isValid, isDirty, isSubmitDisabled, isGlobalSurnamePage }) {
  const [t] = useTranslation()

  return (
    <DialogOverlayActions>
      <Button onClick={overlay.close}>{t('common-ui:cancel.action', 'Cancel')}</Button>
      <Button
        type="submit"
        emphasis="high"
        disabled={isSubmitDisabled || !isValid || !isDirty}
        linkName={
          isGlobalSurnamePage ? 'Button:Selected Global Surname:Submit' : 'Button:Selected Chinese Surname:Submit'
        }
      >
        {t('common-ui:submit.action', 'Submit')}
      </Button>
    </DialogOverlayActions>
  )
}

function FormContent({ focusRef, onChange, onSurnameSelect, isGlobalSurnamePage }) {
  const [focused, setFocused] = useState(false)
  const [inputText, setInputText] = useState('')
  const [surnameSuggestion, setSurnameSuggestion] = useState()
  const [t] = useTranslation()

  useEffect(() => {
    if (!isGlobalSurnamePage && !inputText) {
      setSurnameSuggestion()
    }
  }, [inputText, isGlobalSurnamePage])

  return (
    <Row>
      <Paragraph noShrinkX>{t('LayoutPickerOverlay.surnameSelection.paragraph', 'Enter a surname:')}</Paragraph>
      {isGlobalSurnamePage ? (
        <FormTextField
          ref={focusRef}
          onChange={onChange}
          name="surname"
          type="text"
          autoComplete="false"
          data-testid="global-surname-text-field"
          growX
        />
      ) : (
        <ChineseSurnamePicker
          data-testid="surname-text-field"
          filterSuggestions={(suggestions) =>
            suggestions.filter((suggestion) => suggestion.id <= SURNAMES_WITH_COMPLETE_DATA_AND_IMAGES)
          }
          growX
          onBlur={() => setFocused(false)}
          onChange={(event) => {
            setInputText(event.currentTarget.value)
            onChange(event)
          }}
          onFocus={() => setFocused(true)}
          onSuggestionSelected={(suggestion) => {
            setSurnameSuggestion(suggestion)
            onSurnameSelect(suggestion)
          }}
          placeholder="Li"
          ref={focusRef}
          warningValidate={() =>
            !focused &&
            inputText &&
            !surnameSuggestion &&
            t(
              'LayoutPickerOverlay.surnameSelection.error',
              'Surname not found. Please enter Hanyu Pinyin, Chinese characters, or try a different surname.'
            )
          }
        />
      )}
    </Row>
  )
}

export default function SurnameSelectionFormView({ overlay, pageNumber, focusRef, isGlobalSurnamePage = false }) {
  const [t] = useTranslation()
  const { fetchNextSurnameColor } = useChineseSurnameInfo()
  const { updatePageLayout, addBookPages } = useMemoryBook() || {}
  const { isTabletOrSmaller } = useDeviceSize()
  const [selectedSurnameId, setSelectedSurnameId] = useState(null)

  const validate = (values) => {
    const errors = {}

    if (isGlobalSurnamePage) {
      // Validation for global surnames
      const surnameValue = values.surname
      if (surnameValue) {
        // Allow letters (including accented characters), spaces, hyphens, apostrophes, and periods
        const validSurnamePattern = /^[a-zA-ZÀ-ÿĀ-žА-я\u4e00-\u9fff\s\-'.]+$/

        if (!validSurnamePattern.test(surnameValue)) {
          errors.surname = {
            message: t(
              'LayoutPickerOverlay.surnameSelection.invalidChars',
              'Please enter only letters, spaces, hyphens, and apostrophes. Numbers and special symbols are not allowed.'
            ),
          }
        }
      }
    }

    return { values, errors }
  }

  const methods = useZionForm({
    mode: 'all',
    resolver: validate,
    defaultValues: {
      surname: '',
    },
  })

  const {
    reset,
    formState: { isValid, isDirty },
    getValues,
    setValue,
    trigger,
  } = methods

  useEffect(() => {
    if (!overlay.isOpen && !overlay.transitioning) {
      reset()
      setSelectedSurnameId(null)
    }
  }, [reset, overlay])

  async function onSubmit() {
    const values = getValues()
    const surnameId = isGlobalSurnamePage ? values.surname : selectedSurnameId
    if (surnameId) {
      if (isGlobalSurnamePage) {
        // Handle global surname page logic
        try {
          const surnameData = await fetchSurnameData(surnameId)

          if (!surnameData) {
            console.error(`No surname data found for ${surnameId}`)
            return
          }

          // Process paragraphs similar to GlobalSurnamePageWrapper
          const arrayParagraphs = processHtmlDefinition(surnameData?.htmlDefinition)

          // Calculate paragraphs for first page
          const { remainingParagraphs } = calculatePageParagraphs(arrayParagraphs)

          // If we have remaining paragraphs, create a second page
          if (remainingParagraphs.length > 0) {
            // Add a new page after the current one
            await addBookPages(1, pageNumber, isTabletOrSmaller)

            // Update the second page with remaining content
            await updatePageLayout(pageNumber + 1, 17, {
              surnameId,
              surnamePage: '2',
            })
          }

          // Update the first page with initial content
          await updatePageLayout(pageNumber, 17, {
            surnameId,
            surnamePage: '1',
          })
        } catch (error) {
          console.error('Error in global surname submission:', error)
          return
        }
      } else {
        // Handle Chinese surname page logic
        const color = fetchNextSurnameColor()
        updatePageLayout(pageNumber, 2, { surnameId, color })
      }
    }

    overlay.close()
  }

  function onChange() {
    setSelectedSurnameId(null)

    if (isGlobalSurnamePage) {
      trigger('surname')
    }
  }

  function onSurnameSelect(suggestion) {
    setValue('surname', suggestion.primaryText, { shouldDirty: true })
    setSelectedSurnameId(suggestion.id)
  }

  return (
    <ZionForm methods={methods} onSubmit={onSubmit}>
      <FormContent
        focusRef={focusRef}
        onChange={onChange}
        onSurnameSelect={onSurnameSelect}
        isGlobalSurnamePage={isGlobalSurnamePage}
      />
      <Separator />
      <FormActions
        overlay={overlay}
        isValid={isValid}
        isDirty={isDirty}
        isGlobalSurnamePage={isGlobalSurnamePage}
        isSubmitDisabled={
          (!isGlobalSurnamePage && !selectedSurnameId) || (isGlobalSurnamePage && (!isValid || !isDirty))
        }
      />
    </ZionForm>
  )
}
